import React, { useCallback, useMemo } from 'react';
import EditorRemoveEmojiIcon from '@atlaskit/icon/glyph/editor/remove-emoji';
import { css } from '@emotion/core';
import Select from '@atlaskit/select';
import { useQuery } from '@tanstack/react-query';
import Spinner from '@atlaskit/spinner';
import { Label } from '@atlaskit/form';
import { AllowedTagsDTOFromJSON } from '@atlassiansox/phobos-client-ts';

import { FormFieldTooltipLabel } from '@/components/primitive';
import { borderColor, flexStart } from '@/styles';
import { useFetchErrorController } from '@/components/primitive/error-boundary/hooks';
import { querySpec } from '@/services/api-service/api-queries';
import { apiFetch } from '@/services';

import { helperCopy } from '../../copy';

export interface TagsValue {
  [key: string]: string | undefined;
}

interface TagsPickerProps {
  value: TagsValue;
  onChange: (value: any) => void;
  isDisabled: boolean;
  isInvalid: boolean;
}

// Copied from Performance SLO component
const tagStyles = css`
  margin-top: 0.5rem;
  padding-left: 1rem;
  border-left: 2px solid ${borderColor};
`;

// Copied from Field component
const tagIndividualStyles = css`
  margin-top: 8px;
`;

const slaExclusiveOptionStyles = css`
  font-weight: 500;
`;

const slaExclusiveOptionTerm = 'affect SLA covered experience';
export const slaCoveredExperienceKey: string = 'cloud_sla_covered_experience';
export const slaCriticalPathKey: string = 'cloud_sla_critical_path';

const TagsInput: React.FC<TagsPickerProps> = ({ value, onChange, isInvalid, isDisabled }) => {
  const { onError } = useFetchErrorController();
  const { queryKey, path } = querySpec('tags.allowedValues', {});
  const { isInitialLoading: isLoading, data } = useQuery(queryKey, () => apiFetch(path), {
    onError,
  });

  const handleSLACoveredExperienceChange = useCallback(
    (selectedOptions: FieldOption<string>[]) => {
      let results = [...selectedOptions];
      if (results?.length === 0) {
        const c = { ...value };
        delete c[slaCoveredExperienceKey];
        onChange(c);
      } else {
        if (
          results.length > 1 &&
          results.some((x: FieldOption<string>) => x.value.includes(slaExclusiveOptionTerm))
        ) {
          // if user selects new experience after selecting 'Doesn't
          // affect SLA covered experience', we should let them do that
          // and also remove the 'Doesn't affect SLA covered experience'
          // option, otherwise we should remove all other options.
          if (results[0].value.includes(slaExclusiveOptionTerm)) {
            results.shift();
          } else {
            results = results.filter((x: FieldOption<string>) =>
              x.value.includes(slaExclusiveOptionTerm)
            );
          }
        }
        onChange({
          ...value,
          [slaCoveredExperienceKey]: results.map(x => x.value).join(','),
        });
      }
    },
    [value, onChange]
  );

  const allowedTags = AllowedTagsDTOFromJSON(data)?.tags || [];
  const slaCoveredExperienceOptions = useMemo(
    () =>
      (allowedTags[slaCoveredExperienceKey]?.allowedValues || []).map((x: string) => ({
        // Add remove icon to the 'doesn't affect experience' option that is
        // exclusive to the others
        label: x.includes(slaExclusiveOptionTerm) ? (
          <span css={[flexStart, slaExclusiveOptionStyles]}>
            <EditorRemoveEmojiIcon label="edit" size="small" />
            &nbsp;
            <span>{x}</span>
          </span>
        ) : (
          x
        ),
        value: x,
      })),
    [allowedTags]
  );
  const slaCoveredExperienceValue = useMemo(
    () =>
      slaCoveredExperienceKey in value
        ? (value[slaCoveredExperienceKey] ?? '').split(',').map((x: string) => ({
            label: x,
            value: x,
          }))
        : null,
    [value]
  );

  // We want to order the tags in the following specific way:
  // 1. SLA Covered Experience
  // 2. SLA Critical Path
  // 3. Rest
  const orderedTags = useMemo(() => {
    const entries = Object.entries(allowedTags);
    const entriesOrdeded = entries.filter(
      ([key]) => key !== slaCoveredExperienceKey && key !== slaCriticalPathKey
    );
    const slaCriticalPathEntry = entries.find(([key]) => key === slaCriticalPathKey);
    if (slaCriticalPathEntry) {
      entriesOrdeded.unshift(slaCriticalPathEntry);
    }
    const slaCoveredExperienceEntry = entries.find(([key]) => key === slaCoveredExperienceKey);
    if (slaCoveredExperienceEntry) {
      entriesOrdeded.unshift(slaCoveredExperienceEntry);
    }
    return entriesOrdeded;
  }, [allowedTags]);

  if (isLoading) {
    return (
      <div css={tagStyles}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div data-testid="tags-input" css={tagStyles}>
      {orderedTags.map(([allowedTagKey, allowedTagDetail]) => (
        <div key={`form-crud-input-tag-key-${allowedTagKey}`} css={tagIndividualStyles}>
          <Label htmlFor={`form-crud-input-tag-select-${allowedTagKey}`}>
            {allowedTagKey === slaCoveredExperienceKey && (
              <FormFieldTooltipLabel
                label={`${allowedTagDetail!.friendlyKey} (${allowedTagKey})`}
                content={helperCopy.slo.tagsCoveredExperiences}
              />
            )}
            {allowedTagKey === slaCriticalPathKey && (
              <FormFieldTooltipLabel
                label={`${allowedTagDetail!.friendlyKey} (${allowedTagKey})`}
                content={helperCopy.slo.tagsCriticalPath}
              />
            )}
            {allowedTagKey !== slaCoveredExperienceKey && allowedTagKey !== slaCriticalPathKey && (
              <>
                {allowedTagDetail!.friendlyKey}&nbsp;({allowedTagKey})
              </>
            )}
          </Label>
          {allowedTagKey === slaCoveredExperienceKey && (
            <Select
              inputId={`form-crud-input-tag-select-${allowedTagKey}`}
              className="multi-select"
              classNamePrefix="react-select"
              isMulti
              isSearchable
              isClearable
              placeholder="Select experiences..."
              options={slaCoveredExperienceOptions}
              value={slaCoveredExperienceValue}
              onChange={handleSLACoveredExperienceChange}
              isDisabled={isDisabled}
            />
          )}
          {allowedTagKey !== slaCoveredExperienceKey && (
            <Select
              inputId={`form-crud-input-tag-select-${allowedTagKey}`}
              className="single-select"
              classNamePrefix="react-select"
              options={allowedTagDetail!.allowedValues.map(x => ({ label: x, value: x }))}
              value={
                allowedTagKey in value
                  ? {
                      label: value[allowedTagKey],
                      value: value[allowedTagKey],
                    }
                  : null
              }
              onChange={selectedValue => {
                if (selectedValue === null) {
                  const c = { ...value };
                  delete c[allowedTagKey];
                  onChange(c);
                } else {
                  onChange({ ...value, [allowedTagKey]: selectedValue.value });
                }
              }}
              isClearable
              isDisabled={isDisabled}
              validationState={isInvalid ? 'error' : 'default'} // https://product-fabric.atlassian.net/browse/DSP-821
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default TagsInput;
